import React, { useState, useEffect } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { ExternalLink, Handshake, Mail, Users } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/partners`);
        setPartners(response.data);
      } catch (err) {
        console.error('Error fetching partners:', err);
        // Set default Aspiritech partner if API fails
        setPartners([{
          id: 'default-aspiritech',
          name: 'Aspiritech',
          logo: '',
          image: 'https://customer-assets.emergentagent.com/job_honoree-portal/artifacts/nbv3rmt5_Screenshot%202026-01-12%20at%201.30.25%E2%80%AFPM.png',
          website: 'https://www.aspiritech.org',
          description: 'Aspiritech is a world-class quality assurance company that harnesses the power of neurodiversity. More than 90 percent of their team members are on the autism spectrum. They provide excellence-powered tech solutions including quality assurance, accessibility testing, data services, automation, web development, and more. At Aspiritech, they believe that when you pair an individual\'s strengths with a business need and an uplifting environment, there is no limit to what they can achieve.',
          is_founding: true
        }]);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, []);

  const foundingPartners = partners.filter(p => p.is_founding);
  const otherPartners = partners.filter(p => !p.is_founding);

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center p-3 bg-amber-500/10 rounded-xl mb-6">
            <Handshake className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-white">Founding </span>
            <span className="text-amber-500">Partners</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            We're proud to partner with organizations that share our commitment to honoring Leonard Nimoy's legacy and championing neurodiversity.
          </p>
        </div>
      </section>

      {/* Founding Partners Section */}
      <section className="py-16 bg-[#0a0a0b]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
            </div>
          ) : (
            <div className="space-y-12">
              {foundingPartners.map((partner) => (
                <div 
                  key={partner.id}
                  className="bg-[#141416] border border-gray-800/50 rounded-3xl overflow-hidden"
                >
                  {/* Stacked Layout - Image on top, content below */}
                  <div className="flex flex-col">
                    {/* Image - Full Width */}
                    {partner.image && (
                      <div className="w-full">
                        <img
                          src={partner.image}
                          alt={partner.name}
                          className="w-full h-80 md:h-96 object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}
                    
                    {/* Content - Below Image */}
                    <div className="p-8 lg:p-12">
                      {/* Logo */}
                      {partner.logo && (
                        <img
                          src={partner.logo}
                          alt={`${partner.name} logo`}
                          className="h-14 w-auto mb-6 object-contain"
                        />
                      )}
                      
                      <div className="inline-flex items-center gap-2 mb-4">
                        <span className="px-3 py-1 bg-amber-500/10 text-amber-500 text-sm font-semibold rounded-full">
                          Founding Partner
                        </span>
                      </div>
                      
                      <h2 className="text-3xl font-bold text-white mb-4">
                        {partner.name}
                      </h2>
                      
                      <p className="text-gray-300 mb-6 leading-relaxed max-w-4xl">
                        {partner.description}
                      </p>
                      
                      {partner.website && (
                        <a
                          href={partner.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all w-fit"
                        >
                          Visit Website
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Other Partners */}
              {otherPartners.length > 0 && (
                <>
                  <h3 className="text-2xl font-bold text-white text-center mt-16 mb-8">
                    Our Partners
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {otherPartners.map((partner) => (
                      <div 
                        key={partner.id}
                        className="bg-[#141416] border border-gray-800/50 rounded-2xl p-6 hover:border-amber-500/30 transition-all"
                      >
                        {partner.logo && (
                          <img
                            src={partner.logo}
                            alt={`${partner.name} logo`}
                            className="h-10 w-auto mb-4 object-contain"
                          />
                        )}
                        <h4 className="text-xl font-bold text-white mb-2">{partner.name}</h4>
                        <p className="text-gray-400 text-sm mb-4 line-clamp-3">{partner.description}</p>
                        {partner.website && (
                          <a
                            href={partner.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-amber-500 text-sm font-medium hover:text-amber-400 flex items-center gap-1"
                          >
                            Learn More <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Become a Partner CTA */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 rounded-3xl p-8 md:p-12 text-center">
            <div className="inline-flex items-center justify-center p-4 bg-amber-500/10 rounded-full mb-6">
              <Users className="w-10 h-10 text-amber-500" />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Become Our Next Partner
            </h2>
            
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Join us in celebrating Leonard Nimoy's legacy and championing neurodiversity. 
              We're looking for organizations that share our values and want to make a difference.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@rememberingleonardnimoy.org?subject=Partnership Inquiry"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-full transition-all hover:scale-105"
              >
                <Mail className="w-5 h-5" />
                Contact Us About Partnership
              </a>
            </div>
            
            <p className="text-gray-500 text-sm mt-6">
              info@rememberingleonardnimoy.org
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnersPage;
